import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../theme';

interface FormField {
  id: string;
  type: 'text' | 'dropdown' | 'checkbox';
  label: string;
  required: boolean;
  options?: string[]; // For dropdown
  conditionalLogic?: {
    dependsOn: string; // Field ID this field depends on
    showWhen: string; // Value that triggers showing this field
  };
}

interface FormSchema {
  id: string;
  title: string;
  description: string;
  fields: FormField[];
}

interface FormBuilderProps {
  onSave: (form: FormSchema) => void;
  initialForm?: FormSchema;
}

const FormBuilder: React.FC<FormBuilderProps> = ({ onSave, initialForm }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { control, handleSubmit, watch, setValue, getValues } = useForm<FormSchema>({
    defaultValues: initialForm || {
      id: '',
      title: '',
      description: '',
      fields: []
    }
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'fields'
  });

  const watchedFields = watch('fields');

  const addField = (type: 'text' | 'dropdown' | 'checkbox') => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      type,
      label: `New ${type} field`,
      required: false,
      options: type === 'dropdown' ? ['Option 1', 'Option 2'] : undefined,
    };
    append(newField);
  };

  const editField = (field: FormField) => {
    setEditingField(field);
    setEditDialogOpen(true);
  };

  const saveFieldEdit = (updatedField: FormField) => {
    const fieldIndex = fields.findIndex(f => f.id === updatedField.id);
    if (fieldIndex !== -1) {
      update(fieldIndex, updatedField);
    }
    setEditDialogOpen(false);
    setEditingField(null);
  };

  const onSubmit = (data: FormSchema) => {
    const formWithId = {
      ...data,
      id: data.id || `form_${Date.now()}`
    };
    onSave(formWithId);
  };

  const previewForm = () => {
    setPreviewOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: SEARCE_COLORS.background, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h4" sx={{ color: SEARCE_COLORS.textPrimary, mb: 3, fontWeight: 700 }}>
            Form Builder
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Form Basic Info */}
            <Card sx={{ mb: 4, border: `1px solid ${SEARCE_COLORS.neutral_200}` }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: SEARCE_COLORS.textPrimary }}>
                  Form Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Form Title"
                      variant="outlined"
                      {...control.register('title', { required: true })}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Form ID"
                      variant="outlined"
                      {...control.register('id')}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      variant="outlined"
                      multiline
                      rows={3}
                      {...control.register('description')}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Add Field Buttons */}
            <Card sx={{ mb: 4, border: `1px solid ${SEARCE_COLORS.neutral_200}` }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: SEARCE_COLORS.textPrimary }}>
                  Add Fields
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addField('text')}
                    sx={{
                      borderColor: SEARCE_COLORS.primary,
                      color: SEARCE_COLORS.primary,
                      '&:hover': {
                        borderColor: SEARCE_COLORS.primaryDark,
                        backgroundColor: 'rgba(0, 100, 255, 0.04)',
                      },
                    }}
                  >
                    Add Text Field
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addField('dropdown')}
                    sx={{
                      borderColor: SEARCE_COLORS.primary,
                      color: SEARCE_COLORS.primary,
                      '&:hover': {
                        borderColor: SEARCE_COLORS.primaryDark,
                        backgroundColor: 'rgba(0, 100, 255, 0.04)',
                      },
                    }}
                  >
                    Add Dropdown
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addField('checkbox')}
                    sx={{
                      borderColor: SEARCE_COLORS.primary,
                      color: SEARCE_COLORS.primary,
                      '&:hover': {
                        borderColor: SEARCE_COLORS.primaryDark,
                        backgroundColor: 'rgba(0, 100, 255, 0.04)',
                      },
                    }}
                  >
                    Add Checkbox
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Form Fields */}
            <Card sx={{ mb: 4, border: `1px solid ${SEARCE_COLORS.neutral_200}` }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: SEARCE_COLORS.textPrimary }}>
                  Form Fields ({fields.length})
                </Typography>
                {fields.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No fields added yet. Click the buttons above to add fields.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {fields.map((field, index) => (
                      <Grid item xs={12} key={field.id}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                            borderRadius: 2,
                            '&:hover': {
                              borderColor: SEARCE_COLORS.primary,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip
                                  label={field.type}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                                {field.required && (
                                  <Chip
                                    label="Required"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {field.label}
                              </Typography>
                              {field.type === 'dropdown' && field.options && (
                                <Typography variant="body2" color="text.secondary">
                                  Options: {field.options.join(', ')}
                                </Typography>
                              )}
                              {field.conditionalLogic && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  Shows when: {field.conditionalLogic.dependsOn} = {field.conditionalLogic.showWhen}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => editField(field)}
                                sx={{ color: SEARCE_COLORS.primary }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => remove(index)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={previewForm}
                sx={{
                  borderColor: SEARCE_COLORS.primary,
                  color: SEARCE_COLORS.primary,
                }}
              >
                Preview
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{
                  backgroundColor: SEARCE_COLORS.primary,
                  '&:hover': {
                    backgroundColor: SEARCE_COLORS.primaryDark,
                  },
                }}
              >
                Save Form
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Field Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Field</DialogTitle>
        <DialogContent>
          {editingField && (
            <FieldEditForm
              field={editingField}
              onSave={saveFieldEdit}
              onCancel={() => setEditDialogOpen(false)}
              allFields={fields}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Form Preview</DialogTitle>
        <DialogContent>
          <FormPreview fields={watchedFields} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Field Edit Form Component
interface FieldEditFormProps {
  field: FormField;
  onSave: (field: FormField) => void;
  onCancel: () => void;
  allFields: FormField[];
}

const FieldEditForm: React.FC<FieldEditFormProps> = ({ field, onSave, onCancel, allFields }) => {
  const [editedField, setEditedField] = useState<FormField>(field);

  const handleSave = () => {
    onSave(editedField);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Field Label"
            value={editedField.label}
            onChange={(e) => setEditedField({ ...editedField, label: e.target.value })}
            sx={{ mb: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={editedField.required}
                onChange={(e) => setEditedField({ ...editedField, required: e.target.checked })}
              />
            }
            label="Required"
          />
        </Grid>
        {editedField.type === 'dropdown' && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Options (one per line):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editedField.options?.join('\n') || ''}
              onChange={(e) => setEditedField({
                ...editedField,
                options: e.target.value.split('\n').filter(opt => opt.trim())
              })}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Conditional Logic (Optional):
          </Typography>
          <FormControl fullWidth sx={{ mb: 1 }}>
            <InputLabel>Depends on field</InputLabel>
            <Select
              value={editedField.conditionalLogic?.dependsOn || ''}
              onChange={(e) => setEditedField({
                ...editedField,
                conditionalLogic: {
                  ...editedField.conditionalLogic,
                  dependsOn: e.target.value,
                  showWhen: ''
                } as any
              })}
            >
              <MenuItem value="">No dependency</MenuItem>
              {allFields
                .filter(f => f.id !== field.id && f.type === 'dropdown')
                .map(f => (
                  <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>
                ))}
            </Select>
          </FormControl>
          {editedField.conditionalLogic?.dependsOn && (
            <TextField
              fullWidth
              label="Show when value is"
              value={editedField.conditionalLogic?.showWhen || ''}
              onChange={(e) => setEditedField({
                ...editedField,
                conditionalLogic: {
                  ...editedField.conditionalLogic,
                  showWhen: e.target.value
                } as any
              })}
            />
          )}
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </Box>
    </Box>
  );
};

// Form Preview Component
interface FormPreviewProps {
  fields: FormField[];
}

const FormPreview: React.FC<FormPreviewProps> = ({ fields }) => {
  const { register, handleSubmit, watch } = useForm();

  const onSubmit = (data: any) => {
    // console.log('Form submitted:', data);
  };

  const watchedValues = watch();

  const shouldShowField = (field: FormField) => {
    if (!field.conditionalLogic) return true;
    
    const dependsOnValue = watchedValues[field.conditionalLogic.dependsOn];
    return dependsOnValue === field.conditionalLogic.showWhen;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Form Preview
      </Typography>
      {fields.map((field) => {
        if (!shouldShowField(field)) return null;

        return (
          <Box key={field.id} sx={{ mb: 3 }}>
            {field.type === 'text' && (
              <TextField
                fullWidth
                label={field.label}
                required={field.required}
                {...register(field.id)}
              />
            )}
            {field.type === 'dropdown' && (
              <FormControl fullWidth>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  label={field.label}
                  required={field.required}
                  {...register(field.id)}
                >
                  {field.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {field.type === 'checkbox' && (
              <FormControlLabel
                control={<Checkbox {...register(field.id)} />}
                label={field.label}
              />
            )}
          </Box>
        );
      })}
      <Button type="submit" variant="contained" sx={{ mt: 2 }}>
        Submit Form
      </Button>
    </form>
  );
};

export default FormBuilder;
